import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getTruthFact, mapApiTruthFactToUi } from '../../../services/truth.js';
import { apiErrorStatus } from './apiErrorStatus.js';

/**
 * One Truth fact's detail, for the slide-out (CT-5719). A 404 or 403 is its own
 * outcome — the link outlived the fact, or the viewer may not open it — kept
 * apart from a failure that a retry might fix.
 */
export function useTruthFact({ entityType, entityId, factId } = {}) {
  const [fact, setFact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const fetchIdRef = useRef(0);

  // `quiet` re-reads after a write (a report) without blanking the panel.
  const fetchData = useCallback(
    async ({ quiet = false } = {}) => {
      if (!factId) return;
      const requestId = ++fetchIdRef.current;
      if (!quiet) {
        setLoading(true);
        setError(null);
        setStatus(null);
      }
      try {
        const { body } = await getTruthFact({ entityType, entityId, factId });
        if (requestId !== fetchIdRef.current) return;
        setFact(body ? mapApiTruthFactToUi(body) : null);
      } catch (err) {
        if (requestId !== fetchIdRef.current || quiet) return;
        setFact(null);
        setStatus(apiErrorStatus(err));
        setError(err?.message ?? 'Failed to load the fact');
      } finally {
        if (requestId === fetchIdRef.current) setLoading(false);
      }
    },
    [entityType, entityId, factId],
  );

  const refetch = useCallback(() => fetchData(), [fetchData]);
  const refresh = useCallback(() => fetchData({ quiet: true }), [fetchData]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  return useMemo(
    () => ({ fact, loading, error, notFound: status === 404, accessDenied: status === 403, refetch, refresh }),
    [fact, loading, error, status, refetch, refresh],
  );
}

export default useTruthFact;
